import { loadRemoteModule } from '@angular-architects/module-federation';
import { environment } from '../../environments/environment';
import {
  AssessmentReportingType,
  NoScoreDash,
} from '../multi-stage-growth-report/models/multi-stage-growth-report.model';
import { PlacementLevelCode } from '../pages/domain-summary/models/domain-summary.constant';
import { PerformanceBandCriteria } from '../pages/overall-scale-score-summary/models/overall-scale-score-summary.model';
import { PlacementLevel, RemoteModuleConfig } from '../shared/models/common.model';
import { FLAG_VALUE_ON, FMS_FLAG, REALIZE_FMS_ATTRIBUTES } from './models/constants';

export function getPlacementLevelStaticLabel(
  placementLevel: PlacementLevel[],
  placementLevelKey: PlacementLevelCode | PerformanceBandCriteria,
): string {
  const placementLevelMap = new Map(
    placementLevel.map((placementLevelValue) => [placementLevelValue.placementLevelCode, placementLevelValue]),
  );
  let placementLevelStaticLabel = '';
  switch (placementLevelKey) {
    case PlacementLevelCode.DNM:
      placementLevelStaticLabel = `< ${placementLevelMap.get(PlacementLevelCode.APP).minimumValue}`;
      break;
    case PlacementLevelCode.APP:
      placementLevelStaticLabel = `${placementLevelMap.get(PlacementLevelCode.APP).minimumValue}-${
        placementLevelMap.get(PlacementLevelCode.APP).maximumValue
      }`;
      break;
    case PlacementLevelCode.MET:
      placementLevelStaticLabel = `${placementLevelMap.get(PlacementLevelCode.MET).minimumValue}-${
        placementLevelMap.get(PlacementLevelCode.MET).maximumValue
      }`;
      break;
    case PlacementLevelCode.EXC:
      placementLevelStaticLabel = `> ${placementLevelMap.get(PlacementLevelCode.MET).maximumValue}`;
      break;
  }
  return placementLevelStaticLabel;
}

export function getStaticLableClass(placementLevelCodeId: string): string {
  switch (placementLevelCodeId) {
    case PlacementLevelCode.DNM:
      return 'static-label-text-red';
    case PlacementLevelCode.APP:
      return 'static-label-text-yellow';
    case PlacementLevelCode.MET:
      return 'static-label-text-green';
    case PlacementLevelCode.EXC:
      return 'static-label-text-blue';
    default:
      return null;
  }
}

export const capitalizeFirstLetter = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const getStudentFirstName = (studentName: string): string => {
  const [lastName = '', firstName = ''] = (studentName ?? '').split(',');
  return firstName.trim() || lastName.trim().split(' ')[0];
};

export const extractNumberValue = (input: string): number => {
  const match = input.match(/^([A-Z]*)(\d+).$/);
  if (!match) {
    throw new Error('Invalid input format');
  }

  const [, prefix, numStr] = match;
  const number = parseInt(numStr, 10);

  return prefix ? -number : number;
};

export const getDifferenceScoreAsDelimiter = (reportAScore: string, reportBScore: string): string => {
  const suffix = reportAScore.match(/[A-Za-z]+$/)[0] || reportBScore.match(/[A-Za-z]+$/)[0];
  return `${extractNumberValue(reportAScore) - extractNumberValue(reportBScore)}${suffix}`;
};

export const calculateGrowthScoreFromMetrics = (metrics: Map<AssessmentReportingType, string>): string => {
  const sortedReportsValues = Array.from(metrics.values());
  if (metrics.size > 1) {
    return getDifferenceScoreAsDelimiter(sortedReportsValues[metrics.size - 1], sortedReportsValues[0]);
  } else {
    return NoScoreDash.DASH;
  }
};

export const transformPercentiles = (percentiles: { key: string, value: string }[]): Record<string, string> => {
  return (
    percentiles?.reduce((acc, { key, value }) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, string>) || {}
  );
};

export const convertFloatToPercentile = (value: number): number => {
  return value ? Math.round(value * 100) : 0;
};

export const sortStudentsByNameThenScore = (
  a: { studentName: string, score: string | number },
  b: { studentName: string, score: string | number },
): number => {
  const result = sortNamesAlphabetically(a, b); // sort result based on name
  // If names are identical, sort by score (descending order - highest scores first)
  return result === 0 ? compareScoresWithDelimiterHandling(a.score, b.score) : result;
};

export const sortNamesAlphabetically = (a: { studentName: string }, b: { studentName: string }): number => {
  return a.studentName.localeCompare(b.studentName);
};

export const compareScoresWithDelimiterHandling = (
  studentAScore: string | number,
  studentBScore: string | number,
  delimiter = NoScoreDash.DASH,
): number => {
  // If both scores are null/undefined or delimiters, consider them equal
  if ((!studentAScore || studentAScore === delimiter) && (!studentBScore || studentBScore === delimiter)) return 0;
  // If only studentAScore is null/undefined or delimiter, it is considered less
  if (!studentAScore || studentAScore === delimiter) return 1;
  // If only studentBScore is null/undefined or delimiter, studentAScore is considered greater
  if (!studentBScore || studentBScore === delimiter) return -1;

  // Convert scores to numbers for comparison
  const scoreA = typeof studentAScore === 'string' ? extractNumberValue(studentAScore) : studentAScore;
  const scoreB = typeof studentBScore === 'string' ? extractNumberValue(studentBScore) : studentBScore;

  // Return the difference for descending order (higher scores first)
  return scoreB - scoreA;
};

export const isIPad = (): boolean => {
  const ua = navigator.userAgent;
  const isTouchScreen = 'ontouchstart' in window && navigator.maxTouchPoints > 1;
  const isMacOSLike = /Macintosh/.test(ua);

  return /iPad/.test(ua) || (isMacOSLike && isTouchScreen);
};

export const isAndroidDevice = (): boolean => {
  return navigator.userAgent.toLowerCase().indexOf('android') > -1;
};

export const isNonAppleTouchDevice = (): boolean => {
  const isTouchScreen = 'ontouchstart' in window && navigator.maxTouchPoints > 1;
  const isAppleDevice = /Macintosh|iPad|iPhone/.test(navigator.userAgent);

  return isTouchScreen && !isAppleDevice;
};

/**
 * Dynamically load Search Viewer based on FMS flag
 */
export const loadSearchViewerDynamic = () => {
  const usePathBasedRouting = isFMSFlagEnabled(FMS_FLAG.ENABLE_VIEW_ASSIGN_RESOURCE_PATH_BASED);
  const config = usePathBasedRouting ? environment.resourceSearchConfig : environment.searchViewerConfig;
  return loadModuleFromConfig(config);
};

export const loadModuleFromConfig = async (config: RemoteModuleConfig) => {
  const m = await loadRemoteModule({
    remoteEntry: `${config.remoteEntry}?version=${new Date().toISOString().split(':')[0]}`,
    remoteName: config.remoteName,
    exposedModule: config.exposedModule,
  });
  return m[config.moduleName];
};

export const isFMSFlagEnabled = (flag: FMS_FLAG): boolean => {
  const fmsAttributesStr = sessionStorage.getItem(REALIZE_FMS_ATTRIBUTES);
  const fmsAttributes = fmsAttributesStr ? JSON.parse(fmsAttributesStr) : {};
  return fmsAttributes[flag] === FLAG_VALUE_ON;
};
